import React, { useState, useEffect, useRef, useMemo } from "react";
import jsPDF from "jspdf";
import {
  Search as SearchIcon, Visibility as ViewIcon, CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon, LocationOn as LocationIcon, Photo as PhotoIcon,
  Description as DescriptionIcon, Assignment as AssignmentIcon,
  AccessTime as TimeIcon, Build as BuildIcon, CleaningServices as CleaningIcon,
  Warning as WarningIcon, CheckBox as CheckBoxIcon, ArrowBack as ArrowBackIcon,
  Business as BusinessIcon, DateRange as DateRangeIcon, Event as EventIcon,
  Engineering as EngineeringIcon, Notes as NotesIcon,
  CalendarToday as CalendarIcon, Download as DownloadIcon, Close as CloseIcon
} from "@mui/icons-material";
import { format } from "date-fns";
import { getAuthHeader } from "../../helpers/auth";
import axios from "axios";
import { X, ChevronRight, Activity, CheckCircle, Clock, Calendar } from "lucide-react";

// ── Design tokens ─────────────────────────────────────────────────
const T = {
  bg:       "#080C18",
  surface:  "#0D1120",
  surf2:    "#111827",
  surf3:    "#161E30",
  border:   "rgba(255,255,255,0.07)",
  bord2:    "rgba(255,255,255,0.11)",
  text:     "#E2E8F0",
  muted:    "rgba(255,255,255,0.35)",
  faint:    "rgba(255,255,255,0.10)",
  blue:     "#3B82F6",
  teal:     "#22D3A5",
  amber:    "#FBBF24",
  red:      "#F87171",
  purple:   "#A78BFA",
  pink:     "#F472B6",
  green:    "#34D399",
  font:     "'DM Sans', sans-serif",
  mono:     "'DM Mono', monospace",
};

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&family=DM+Mono:wght@400;500&display=swap');
  @keyframes spin    { to { transform: rotate(360deg); } }
  @keyframes fadeUp  { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
  @keyframes modalIn { from{opacity:0;transform:scale(0.97)} to{opacity:1;transform:scale(1)} }

  .jh-job-card {
    animation: fadeUp 0.32s ease both;
    transition: transform 0.2s cubic-bezier(0.4,0,0.2,1), box-shadow 0.2s;
  }
  .jh-job-card:hover { transform: translateY(-5px); box-shadow: 0 20px 44px rgba(0,0,0,0.5) !important; }

  .jh-nav-card {
    background: #0D1120; border: 1px solid rgba(255,255,255,0.07);
    border-radius: 14px; padding: 18px 20px; cursor: pointer;
    display: flex; align-items: center; gap: 14px;
    transition: all 0.18s; animation: fadeUp 0.3s ease both;
  }
  .jh-nav-card:hover {
    border-color: rgba(59,130,246,0.4); background: rgba(59,130,246,0.06);
    transform: translateY(-2px); box-shadow: 0 8px 24px rgba(0,0,0,0.35);
  }

  .jh-stat-card { transition: transform 0.2s, box-shadow 0.2s; }
  .jh-stat-card:hover { transform: translateY(-3px); box-shadow: 0 12px 28px rgba(0,0,0,0.4) !important; }

  .jh-filter-btn {
    padding: 7px 15px; border-radius: 8px; font-size: 12px; font-weight: 700;
    cursor: pointer; font-family: 'DM Sans', sans-serif;
    border: 1px solid rgba(255,255,255,0.08); transition: all 0.15s;
    background: rgba(255,255,255,0.04); color: rgba(255,255,255,0.4);
    display: flex; align-items: center; gap: 5px;
  }
  .jh-filter-btn:hover { background: rgba(255,255,255,0.08); color: rgba(255,255,255,0.7); }
  .jh-filter-btn.jh-active-all    { background: rgba(255,255,255,0.08); color: #E2E8F0; border-color: rgba(255,255,255,0.18); }
  .jh-filter-btn.jh-active-comm   { background: rgba(59,130,246,0.14); color: #60A5FA; border-color: rgba(59,130,246,0.32); }
  .jh-filter-btn.jh-active-maint  { background: rgba(167,139,250,0.14); color: #A78BFA; border-color: rgba(167,139,250,0.32); }
  .jh-filter-btn.jh-active-done   { background: rgba(52,211,153,0.12); color: #34D399; border-color: rgba(52,211,153,0.28); }
  .jh-filter-btn.jh-active-prog   { background: rgba(251,191,36,0.12); color: #FBBF24; border-color: rgba(251,191,36,0.28); }
  .jh-filter-btn.jh-active-sched  { background: rgba(59,130,246,0.12); color: #60A5FA; border-color: rgba(59,130,246,0.28); }

  .jh-input {
    width: 100%; padding: 9px 11px;
    background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.10);
    border-radius: 9px; color: #E2E8F0; font-size: 13px;
    font-family: 'DM Sans', sans-serif; outline: none; box-sizing: border-box;
    transition: border-color 0.15s, box-shadow 0.15s;
  }
  .jh-input:focus { border-color: rgba(96,165,250,0.5); box-shadow: 0 0 0 3px rgba(96,165,250,0.10); background: rgba(255,255,255,0.07); }
  .jh-input::placeholder { color: rgba(255,255,255,0.2); }

  .jh-label {
    display: block; font-size: 9px; font-weight: 700;
    letter-spacing: 0.12em; text-transform: uppercase;
    color: rgba(255,255,255,0.30); margin-bottom: 5px;
  }

  .jh-btn-primary {
    padding: 10px 20px; border: none; border-radius: 10px; cursor: pointer;
    font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 700;
    background: linear-gradient(135deg,#3B82F6,#22D3A5); color: #fff;
    display: flex; align-items: center; justify-content: center; gap: 6px;
    box-shadow: 0 4px 14px rgba(59,130,246,0.28); transition: opacity 0.15s, transform 0.15s;
  }
  .jh-btn-primary:hover { opacity: 0.9; transform: translateY(-1px); }

  .jh-btn-ghost {
    padding: 10px 20px; border: 1px solid rgba(255,255,255,0.12); border-radius: 10px; cursor: pointer;
    font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 600;
    background: rgba(255,255,255,0.04); color: rgba(255,255,255,0.6);
    display: flex; align-items: center; justify-content: center; gap: 6px;
    transition: background 0.15s;
  }
  .jh-btn-ghost:hover { background: rgba(255,255,255,0.09); color: #E2E8F0; }

  .jh-field-cell {
    background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.07);
    border-radius: 10px; padding: 12px 14px;
  }
  .jh-check-cell {
    display: flex; align-items: center; justify-content: space-between;
    background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.07);
    border-radius: 10px; padding: 10px 14px; transition: all 0.15s;
  }
  .jh-check-cell.active-check {
    background: rgba(52,211,153,0.08); border-color: rgba(52,211,153,0.25);
  }
  .jh-timeline-dot {
    width: 36px; height: 36px; border-radius: 10px; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
  }

  ::-webkit-scrollbar { width: 4px; height: 4px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.10); border-radius: 4px; }
`;

// ── Helpers ───────────────────────────────────────────────────────
const getStatusCfg = (status) => {
  switch (status) {
    case "completed":
      return { color: "#34D399", bg: "rgba(52,211,153,0.10)",  border: "rgba(52,211,153,0.26)",  label: "Completed"   };
    case "started": case "in_progress": case "before_photos": case "after_photos":
      return { color: "#FBBF24", bg: "rgba(251,191,36,0.10)",  border: "rgba(251,191,36,0.26)",  label: "In Progress" };
    case "scheduled":
      return { color: "#60A5FA", bg: "rgba(59,130,246,0.10)",  border: "rgba(59,130,246,0.26)",  label: "Scheduled"   };
    default:
      return { color: "#94A3B8", bg: "rgba(148,163,184,0.08)", border: "rgba(148,163,184,0.20)", label: status        };
  }
};

const getTypeCfg = (type) =>
  type === "maintenance"
    ? { color: "#A78BFA", bg: "rgba(167,139,250,0.12)", border: "rgba(167,139,250,0.28)", label: "Maintenance"  }
    : { color: "#60A5FA", bg: "rgba(59,130,246,0.12)",  border: "rgba(59,130,246,0.28)",  label: "Commercial"   };

const cleanPhotoUrl = (url) => {
  if (!url) return "";
  let u = url.toString().trim().replace(/["\[\]]/g,"").replace(/%22/g,"").replace(/%5D/g,"");
  if (u.startsWith("http")) return u;
  if (u.startsWith("/")) return `https://app.sowashusa.com${u}`;
  return `https://app.sowashusa.com/uploads/maintenance/${u}`;
};

const Overlay = ({ onClick, children, zIndex = 9999 }) => (
  <div onClick={onClick} style={{
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.80)",
    backdropFilter: "blur(5px)", display: "flex", alignItems: "center",
    justifyContent: "center", zIndex, padding: 20,
  }}>{children}</div>
);

const CloseBtn = ({ onClick }) => (
  <button onClick={onClick} style={{
    background: "rgba(255,255,255,0.06)", border: `1px solid ${T.border}`,
    color: T.muted, width: 32, height: 32, borderRadius: 8, cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
    transition: "background 0.15s",
  }}
    onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.12)"}
    onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.06)"}
  ><X size={14} /></button>
);

const SecHead = ({ icon: Icon, label, color }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
    <div style={{ width: 28, height: 28, borderRadius: 8, background: `${color}18`, border: `1px solid ${color}35`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      <Icon style={{ fontSize: 15, color }} />
    </div>
    <span style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{label}</span>
  </div>
);

const FieldCell = ({ label, value, span }) => (
  <div className="jh-field-cell" style={span ? { gridColumn: "1 / -1" } : {}}>
    <div className="jh-label">{label}</div>
    <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{value || "N/A"}</div>
  </div>
);

// ══════════════════════════════════════════════════════════════════
//   JobEfficiencyTimer (Read-only mode for customers)
// ══════════════════════════════════════════════════════════════════
const JobEfficiencyTimer = ({ job }) => {
  const [timerData, setTimerData] = useState({
    remainingTime: 0, isExceeded: false, delayMinutes: 0,
    totalTimeTaken: 0, efficiencyScore: 0
  });

  useEffect(() => {
    const calculate = () => {
      if (!job.started_at) return;
      const actualReached = new Date(job.reached_location_at || job.started_at);
      const shiftStartTime = new Date(actualReached);
      if (job.shift_type === 'afternoon') shiftStartTime.setHours(12, 0, 0, 0);
      else shiftStartTime.setHours(7, 0, 0, 0);
      
      const diffMs = actualReached - shiftStartTime;
      const delay = Math.max(0, Math.floor(diffMs / 60000));
      const teamSize = 4;
      const totalManHours = parseFloat(job.total_man_hours) || 32;
      const targetSec = (totalManHours / teamSize) * 3600;
      const endTime = job.completed_at ? new Date(job.completed_at) : new Date();
      const elapsedSec = Math.floor((endTime - new Date(job.reached_location_at || job.started_at)) / 1000);
      const remaining = targetSec - elapsedSec;
      const efficiency = elapsedSec > 0 ? Math.round((targetSec / elapsedSec) * 100) : 100;
      
      setTimerData({ remainingTime: Math.abs(remaining), isExceeded: remaining < 0, delayMinutes: delay, totalTimeTaken: elapsedSec, efficiencyScore: efficiency });
    };
    if (job.completed_at) { calculate(); }
    else { const itv = setInterval(calculate, 1000); calculate(); return () => clearInterval(itv); }
  }, [job]);

  const fmtTime = (s) => {
    const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
    return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(sec).padStart(2,'0')}`;
  };
  
  if (!job.started_at) return (
    <div style={{ marginTop: 10, padding: "10px 14px", borderRadius: 10, background: "rgba(255,255,255,0.03)", border: "1px dashed rgba(255,255,255,0.10)", textAlign: "center" }}>
      <span style={{ fontSize: 12, color: T.muted, fontWeight: 600 }}>🕒 Waiting for service to start…</span>
    </div>
  );

  const isDone = !!job.completed_at;
  const exceed = timerData.isExceeded;
  const timerColor = isDone ? T.text : (exceed ? T.red : T.green);
  const timerBg   = isDone ? "rgba(255,255,255,0.03)" : (exceed ? "rgba(248,113,113,0.07)" : "rgba(52,211,153,0.07)");
  const timerBrd  = isDone ? T.border : (exceed ? "rgba(248,113,113,0.22)" : "rgba(52,211,153,0.22)");

  return (
    <div style={{ marginTop: 10, padding: 14, borderRadius: 12, background: timerBg, border: `1px solid ${timerBrd}` }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 9, fontWeight: 800, color: timerColor, textTransform: "uppercase", letterSpacing: "0.10em", marginBottom: 3 }}>
            {isDone ? "🏁 Total Time Taken" : (exceed ? "⚠️ Extra Time" : "⏱️ Time Elapsed")}
          </div>
          <div style={{ fontSize: 22, fontWeight: 900, color: timerColor, fontFamily: T.mono, lineHeight: 1 }}>
            {isDone ? fmtTime(timerData.totalTimeTaken) : fmtTime(timerData.totalTimeTaken)}
          </div>
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, borderTop: `1px solid ${T.border}`, paddingTop: 9 }}>
        <div style={{ flex: 1, padding: "5px 9px", borderRadius: 7, background: "rgba(255,255,255,0.03)", border: `1px solid ${T.border}`, textAlign: "center" }}>
          <div style={{ fontSize: 8, color: T.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em" }}>Team</div>
          <div style={{ fontSize: 12, fontWeight: 800, color: T.muted, fontFamily: T.mono }}>Assigned</div>
        </div>
        <div style={{ flex: 1, padding: "5px 9px", borderRadius: 7, background: "rgba(255,255,255,0.03)", border: `1px solid ${T.border}`, textAlign: "center" }}>
          <div style={{ fontSize: 8, color: T.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em" }}>Limit</div>
          <div style={{ fontSize: 12, fontWeight: 800, color: T.muted, fontFamily: T.mono }}>
            {(parseFloat(job.total_man_hours || 32) / 4)}h
          </div>
        </div>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════
//   MAIN CUSTOMER COMPONENT
// ══════════════════════════════════════════════════════════════════
const CustomerJobHistory = () => {
  const config = getAuthHeader();

  const [user, setUser] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [jobs, setJobs]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterType, setFilterType]     = useState("all");
  const [searchQuery, setSearchQuery]   = useState("");

  const [stats, setStats] = useState({ total: 0, completed: 0, inProgress: 0, scheduled: 0, commercial: 0, maintenance: 0 });

  const [viewMode, setViewMode] = useState("clients"); // 'clients' | 'months' | 'dates' | 'jobs'
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedMonth, setSelectedMonth]   = useState(null);
  const [selectedDate, setSelectedDate]     = useState(null);
  const debounceTimer = useRef(null);

  useEffect(() => {
    try {
        const userStr = localStorage.getItem('user');
        if (userStr) setUser(JSON.parse(userStr));
    } catch (err) { console.error('Error loading user data:', err); }
    fetchAllJobs();
  }, []);

  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(fetchAllJobs, 500);
    return () => { if (debounceTimer.current) clearTimeout(debounceTimer.current); };
  }, [filterStatus, filterType, searchQuery]);

  const fetchAllJobs = async () => {
    try {
        setLoading(true);
        const params = { dateRange: 'all' };
        if (filterStatus && filterStatus !== 'all') params.status = filterStatus;
        if (searchQuery && searchQuery.trim() !== '') params.search = searchQuery.trim();

        const [commercialResponse, maintenanceResponse] = await Promise.all([
            axios.get(`${process.env.REACT_APP_API_BASE_URL}/commercial-jobs-history/history`, { params, ...config }),
            axios.get(`${process.env.REACT_APP_API_BASE_URL}/commercial-jobs-history/maintenance-history`, { params, ...config })
        ]);

        const commercialJobs = (commercialResponse.data.jobs || []).map(job => ({
            ...job, job_type: 'commercial', unique_id: `commercial_${job.schedule_id}`
        }));

        const maintenanceJobs = (maintenanceResponse.data.jobs || []).map(job => ({
            ...job, job_type: 'maintenance', unique_id: `maintenance_${job.id}`, schedule_id: job.id
        }));

        let combinedJobs = [...commercialJobs, ...maintenanceJobs];
        combinedJobs.sort((a, b) => new Date(b.scheduled_date) - new Date(a.scheduled_date));

        if (filterType === 'commercial') combinedJobs = combinedJobs.filter(job => job.job_type === 'commercial');
        else if (filterType === 'maintenance') combinedJobs = combinedJobs.filter(job => job.job_type === 'maintenance');

        setJobs(combinedJobs);
        setStats({
            total: combinedJobs.length,
            completed: combinedJobs.filter(j => j.status === 'completed').length,
            inProgress: combinedJobs.filter(j => ['started', 'in_progress', 'before_photos', 'after_photos'].includes(j.status)).length,
            scheduled: combinedJobs.filter(j => j.status === 'scheduled').length,
            commercial: commercialJobs.length,
            maintenance: maintenanceJobs.length,
        });
    } catch (error) {
        console.error('Error fetching jobs:', error);
    } finally {
        setLoading(false);
    }
  };

  const clientGroups = useMemo(() => {
    const groups = {};
    jobs.forEach(job => {
      const name = job.client_name || "Unknown Client";
      if (!groups[name]) groups[name] = [];
      groups[name].push(job);
    });
    return groups;
  }, [jobs]);

  const monthGroups = useMemo(() => {
    if (!selectedClient || !clientGroups[selectedClient]) return {};
    const groups = {};
    clientGroups[selectedClient].forEach(job => {
      const k = format(new Date(job.scheduled_date), "yyyy-MM");
      if (!groups[k]) groups[k] = [];
      groups[k].push(job);
    });
    return groups;
  }, [selectedClient, clientGroups]);

  const dateGroups = useMemo(() => {
    if (!selectedMonth || !monthGroups[selectedMonth]) return {};
    const groups = {};
    monthGroups[selectedMonth].forEach(job => {
      const k = format(new Date(job.scheduled_date), "yyyy-MM-dd");
      if (!groups[k]) groups[k] = [];
      groups[k].push(job);
    });
    return groups;
  }, [selectedMonth, monthGroups]);

  const currentJobsList = useMemo(() => {
    if (!selectedDate || !dateGroups[selectedDate]) return [];
    return dateGroups[selectedDate];
  }, [selectedDate, dateGroups]);

  const handleClientClick = (c) => { setSelectedClient(c); setViewMode("months"); };
  const handleMonthClick  = (m) => { setSelectedMonth(m);  setViewMode("dates");  };
  const handleDateClick   = (d) => { setSelectedDate(d);   setViewMode("jobs");   };
  const handleBack = () => {
    if      (viewMode === "jobs")   { setViewMode("dates");  setSelectedDate(null); }
    else if (viewMode === "dates")  { setViewMode("months"); setSelectedMonth(null); }
    else if (viewMode === "months") { setViewMode("clients"); setSelectedClient(null); setSearchQuery(""); }
  };

  const handleViewDetails = async (job) => {
    try {
      setDetailsLoading(true); setDetailsOpen(true);
      const endpoint = job.job_type === 'commercial' 
          ? `/commercial-jobs-history/${job.schedule_id}/detail`
          : `/commercial-jobs-history/maintenance/${job.schedule_id}/detail`;
          
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}${endpoint}`, config);
      setSelectedJob({ ...response.data.job, job_type: job.job_type });
    } catch (error) {
      console.error("Error fetching job details:", error);
      alert(`Error: ${error.response?.data?.message || error.message}`);
      setDetailsOpen(false);
    } finally { setDetailsLoading(false); }
  };

  // ── PDF logic ─────────────────────────────────────
  const handleDownloadReport = async () => {
    try {
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      let yPosition = 0;
      const colors = {
        primary: [102,126,234], secondary: [118,75,162], success: [16,185,129],
        warning: [245,158,11], info: [59,130,246], dark: [17,24,39],
        gray: [107,114,128], lightGray: [243,244,246],
      };
      const checkNewPage = (req = 40) => {
        if (yPosition + req > pageHeight - 25) { pdf.addPage(); yPosition = margin; return true; }
        return false;
      };
      const addSectionHeader = (title, iconColor = colors.primary) => {
        checkNewPage(20);
        pdf.setFillColor(...iconColor); pdf.circle(margin+3, yPosition+3, 3, "F");
        pdf.setTextColor(...colors.dark); pdf.setFontSize(14); pdf.setFont("helvetica","bold");
        pdf.text(title, margin+10, yPosition+5); yPosition += 12;
      };
      const isMaintenance = (job) => job?.job_type?.toLowerCase() === "maintenance";
      const addImageToPDF = async (imageUrl, x, y, w, h) => {
        try {
          if (!imageUrl) return false;
          let cleanUrl = imageUrl.replace(/["\[\]]/g,"").trim();
          if (!cleanUrl.startsWith("http")) cleanUrl = `https://app.sowashusa.com/uploads/${selectedJob && isMaintenance(selectedJob) ? "maintenance" : "job-photos"}/${cleanUrl}`;
          const img = new Image(); img.crossOrigin = "anonymous"; img.src = cleanUrl;
          await new Promise(res => { img.onload=res; img.onerror=res; setTimeout(res,3000); });
          pdf.addImage(img,"JPEG",x,y,w,h); return true;
        } catch(e) { return false; }
      };
      const drawPhotoGrid = async (title, photos, basePath) => {
        if (!photos || photos.length === 0) return;
        checkNewPage(40); addSectionHeader(title, colors.info);
        const gap=10, boxWidth=(pageWidth-2*margin-gap)/2, boxHeight=60;
        for (let i=0; i<photos.length; i++) {
          const col=i%2;
          if (col===0 && yPosition+boxHeight > pageHeight-20) { pdf.addPage(); yPosition=margin; }
          const xPos = col===0 ? margin : margin+boxWidth+gap;
          let pn = photos[i]; if (pn.includes("/")) pn=pn.split("/").pop(); pn=pn.replace(/["\[\]]/g,"");
          const fullUrl = pn.startsWith("http") ? pn : `${basePath}/${pn}`;
          pdf.setDrawColor(...colors.lightGray); pdf.setLineWidth(0.3); pdf.rect(xPos, yPosition, boxWidth, boxHeight);
          await addImageToPDF(fullUrl, xPos+2, yPosition+2, boxWidth-4, boxHeight-8);
          pdf.setFontSize(8); pdf.setTextColor(...colors.gray); pdf.text(`${title.replace(" PHOTOS","")} ${i+1}`, xPos+2, yPosition+boxHeight-2);
          if (col===1 || i===photos.length-1) yPosition += boxHeight+5;
        }
        yPosition += 5;
      };
      const drawPageHeader = (title, jobData) => {
        yPosition = 0; const headerHeight=65;
        for (let i=0; i<headerHeight; i++) {
          const ratio=i/headerHeight, r=colors.primary[0]+(colors.secondary[0]-colors.primary[0])*ratio, g=colors.primary[1]+(colors.secondary[1]-colors.primary[1])*ratio, b=colors.primary[2]+(colors.secondary[2]-colors.primary[2])*ratio;
          pdf.setFillColor(r,g,b); pdf.rect(0,i,pageWidth,1,"F");
        }
        pdf.setFillColor(255,255,255);
        try { const logoImg=new Image(); logoImg.src="/sowash-logo.png"; pdf.addImage(logoImg,"PNG",margin+60,5,60,15); } catch(e) {}
        yPosition=35; pdf.setTextColor(255,255,255); pdf.setFontSize(26); pdf.setFont("helvetica","bold"); pdf.text(title,pageWidth/2,yPosition,{align:"center"});
        yPosition=48; pdf.setFontSize(11); pdf.text(`Service #${jobData.service_number}`,pageWidth/2,yPosition,{align:"center"});
        yPosition+=6; pdf.setFontSize(8); pdf.setFont("helvetica","normal"); pdf.text(`Generated on ${format(new Date(),"MMMM dd, yyyy")}`,pageWidth/2,yPosition,{align:"center"}); yPosition=75;
      };
      const drawSiteInfo = (jobData) => {
        addSectionHeader("Site Information", colors.info);
        pdf.setFillColor(...colors.lightGray); pdf.roundedRect(margin,yPosition,pageWidth-2*margin,75,3,3,"F"); yPosition+=7;
        const leftCol=margin+5, rightCol=pageWidth/2+2;
        const siteDetails = [
          {label:"SITE NAME",value:jobData.site_name||"N/A"},{label:"SERVICE NUMBER",value:`#${jobData.service_number}`},
          {label:"CLIENT NAME",value:jobData.client_name||"N/A"},{label:"STATUS",value:jobData.status.toUpperCase().replace("_"," ")},
          {label:"JOB TYPE",value:isMaintenance(jobData)?"MAINTENANCE SERVICE":"COMMERCIAL CLEANING"},{label:"TEAM LEAD",value:jobData.team_lead_name||"Not Assigned"},
          {label:"CONTACT PERSON",value:jobData.contact_person||"N/A"},{label:"CONTACT NUMBER",value:jobData.contact_number||"N/A"},
        ];
        if (isMaintenance(jobData)) { if (jobData.task_name) siteDetails.push({label:"TASK",value:jobData.task_name}); }
        else { if (jobData.system_size) siteDetails.push({label:"SYSTEM SIZE",value:`${jobData.system_size} kW`}); }
        siteDetails.forEach((d,idx) => {
          const col=idx%2, row=Math.floor(idx/2), xPos=col===0?leftCol:rightCol, y=yPosition+row*14;
          pdf.setTextColor(...colors.gray); pdf.setFont("helvetica","bold"); pdf.setFontSize(7); pdf.text(d.label,xPos,y);
          pdf.setTextColor(...colors.dark); pdf.setFont("helvetica","normal"); pdf.setFontSize(9); pdf.text(d.value?.toString()||"-",xPos,y+4);
        });
        yPosition+=68;
        if (jobData.address) { pdf.setTextColor(...colors.gray); pdf.setFont("helvetica","bold"); pdf.setFontSize(7); pdf.text("ADDRESS",leftCol,yPosition); pdf.setTextColor(...colors.dark); pdf.setFont("helvetica","normal"); pdf.setFontSize(9); pdf.text(jobData.address,leftCol,yPosition+4); yPosition+=15; } else yPosition+=5;
      };
      const drawTimeline = (jobData) => {
        checkNewPage(70); addSectionHeader("Job Timeline", colors.warning);
        const events = [
          {label:"Scheduled",date:jobData.scheduled_date,color:colors.info},
          {label:"Site Arrival",date:jobData.reached_location_at,color:colors.warning},
          {label:"Work Area Reached",date:jobData.started_at,color:colors.success},
          {label:"Work Started",date:jobData.before_photos_at||jobData.before_photo_at,color:[139,92,246]},
          {label:"Work Completed",date:jobData.after_photos_at||jobData.after_photo_at,color:[236,72,153]},
          {label:"Site Exit",date:jobData.completed_at,color:colors.success},
        ];
        events.forEach((e,i) => {
          if (e.date) {
            pdf.setFillColor(...e.color); pdf.circle(margin+4,yPosition+3,2,"F");
            if (i<events.filter(x=>x.date).length-1) { pdf.setDrawColor(...colors.lightGray); pdf.setLineWidth(0.5); pdf.line(margin+4,yPosition+5,margin+4,yPosition+11); }
            pdf.setTextColor(...e.color); pdf.setFont("helvetica","bold"); pdf.setFontSize(10); pdf.text(e.label,margin+10,yPosition+4.5);
            pdf.setTextColor(...colors.gray); pdf.setFont("helvetica","normal"); pdf.setFontSize(8); pdf.text(format(new Date(e.date),"MMM dd, yyyy - hh:mm a"),margin+60,yPosition+4.5);
            yPosition+=10;
          }
        });
        yPosition+=5;
      };
      const renderJobContent = async (jobData) => {
        const title = isMaintenance(jobData) ? "Technical Maintenance Report" : "Soiling Report";
        drawPageHeader(title,jobData); drawSiteInfo(jobData); drawTimeline(jobData);
        if (isMaintenance(jobData)) {
          checkNewPage(60); addSectionHeader("Maintenance Task Details", colors.secondary);
          if (jobData.task_name) { pdf.setFont("helvetica","bold"); pdf.setFontSize(10); pdf.text(jobData.task_name,margin,yPosition+5); yPosition+=8; }
          if (jobData.task_description||jobData.maintenance_notes) {
            pdf.setFillColor(253,244,255); const desc=jobData.task_description||jobData.maintenance_notes;
            const descLines=pdf.splitTextToSize(desc,pageWidth-2*margin-6); const descHeight=Math.max(descLines.length*5+10,20);
            pdf.roundedRect(margin,yPosition,pageWidth-2*margin,descHeight,2,2,"F"); pdf.setDrawColor(192,38,211); pdf.roundedRect(margin,yPosition,pageWidth-2*margin,descHeight,2,2,"S");
            pdf.setTextColor(...colors.dark); pdf.setFont("helvetica","normal"); pdf.text(descLines,margin+3,yPosition+7); yPosition+=descHeight+10;
          }
          const mChecks=[{label:"Safety Check",value:"Passed"},{label:"Task Status",value:"Completed"},{label:"System Integrity",value:"Verified"},{label:"Cleaning",value:"Done"}];
          mChecks.forEach((c,i) => {
            const col=i%2, row=Math.floor(i/2), boxWidth=(pageWidth-2*margin-5)/2, xPos=margin+col*(boxWidth+5), yPos=yPosition+row*18;
            pdf.setFillColor(253,244,255); pdf.roundedRect(xPos,yPos,boxWidth,15,2,2,"F"); pdf.setDrawColor(...colors.secondary); pdf.roundedRect(xPos,yPos,boxWidth,15,2,2,"S");
            pdf.setTextColor(...colors.gray); pdf.setFont("helvetica","bold"); pdf.text(c.label.toUpperCase(),xPos+3,yPos+5);
            pdf.setTextColor(...colors.dark); pdf.setFont("helvetica","normal"); pdf.text(c.value,xPos+3,yPos+10);
          });
          yPosition+=Math.ceil(mChecks.length/2)*18+10;
        }
        if (!isMaintenance(jobData) && jobData.status==="completed") {
          checkNewPage(100); addSectionHeader("Field Service Report Details", colors.success);
          const fsrConditions = [
            {label:"Cable Condition",value:jobData.cable_condition||"N/A"},{label:"Panel Damage",value:jobData.panel_damage||"N/A"},
            {label:"Inverter Alarm",value:jobData.inverter_alarm||"N/A"},{label:"Potential Shading",value:jobData.potential_shading||"N/A"},
            {label:"Total Washed Panels",value:jobData.total_panels_cleaned||"N/A"},
          ];
          fsrConditions.forEach((c,i) => {
            const col=i%2, row=Math.floor(i/2), boxWidth=(pageWidth-2*margin-5)/2, xPos=margin+col*(boxWidth+5), yPos=yPosition+row*18;
            pdf.setFillColor(255,255,255); pdf.roundedRect(xPos,yPos,boxWidth,15,2,2,"F"); pdf.setDrawColor(...colors.lightGray); pdf.setLineWidth(0.3); pdf.roundedRect(xPos,yPos,boxWidth,15,2,2,"S");
            pdf.setTextColor(...colors.gray); pdf.setFont("helvetica","bold"); pdf.setFontSize(7); pdf.text(c.label.toUpperCase(),xPos+3,yPos+5);
            pdf.setTextColor(...colors.dark); pdf.setFont("helvetica","normal"); pdf.setFontSize(9);
            const vl=pdf.splitTextToSize(c.value?.toString()||"N/A",boxWidth-6); pdf.text(vl,xPos+3,yPos+10);
          });
          yPosition+=60;
        }
        if (jobData.customer_signature) {
          checkNewPage(35); yPosition+=5;
          pdf.setTextColor(...colors.gray); pdf.setFont("helvetica","bold"); pdf.setFontSize(9); pdf.text("CUSTOMER SIGNATURE",margin,yPosition); yPosition+=6;
          const sigUrl=jobData.customer_signature.startsWith("http") ? jobData.customer_signature : `https://app.sowashusa.com/uploads/signatures/${jobData.customer_signature}`;
          await addImageToPDF(sigUrl,margin,yPosition,80,25); yPosition+=30;
        }
        let beforeArr=[], afterArr=[];
        const photoBaseUrl=isMaintenance(jobData)?"https://app.sowashusa.com/uploads/maintenance":"https://app.sowashusa.com/uploads/job-photos";
        if (jobData.before_photos) { try { beforeArr=JSON.parse(jobData.before_photos); } catch(e) { beforeArr=[]; } } else if (jobData.before_photo_url) beforeArr=[jobData.before_photo_url];
        if (jobData.after_photos)  { try { afterArr=JSON.parse(jobData.after_photos);  } catch(e) { afterArr=[];  } } else if (jobData.after_photo_url)  afterArr=[jobData.after_photo_url];
        if (beforeArr.length>0) await drawPhotoGrid("BEFORE PHOTOS",beforeArr,photoBaseUrl);
        if (afterArr.length>0)  await drawPhotoGrid("AFTER PHOTOS", afterArr, photoBaseUrl);
        const remarks=jobData.remarks||jobData.notes;
        if (remarks) {
          checkNewPage(30); yPosition+=3;
          pdf.setTextColor(...colors.gray); pdf.setFont("helvetica","bold"); pdf.setFontSize(9); pdf.text("REMARKS",margin,yPosition); yPosition+=6;
          pdf.setFillColor(239,246,255); const lines=pdf.splitTextToSize(remarks,pageWidth-2*margin-6); const h=lines.length*5+6;
          pdf.roundedRect(margin,yPosition,pageWidth-2*margin,h,2,2,"F"); pdf.setDrawColor(59,130,246); pdf.roundedRect(margin,yPosition,pageWidth-2*margin,h,2,2,"S");
          yPosition+=5; pdf.setTextColor(...colors.dark); pdf.setFont("helvetica","normal"); pdf.setFontSize(8); pdf.text(lines,margin+3,yPosition); yPosition+=h+10;
        }
      };
      await renderJobContent(selectedJob);
      const selDateStr=format(new Date(selectedJob.scheduled_date),"yyyy-MM-dd");
      const mergedJobs=jobs.filter(j=>j.job_type==="maintenance"&&j.status==="completed"&&format(new Date(j.scheduled_date),"yyyy-MM-dd")===selDateStr&&j.unique_id!==selectedJob.unique_id);
      for (const mJob of mergedJobs) { pdf.addPage(); yPosition=15; await renderJobContent(mJob); }
      const totalPages=pdf.internal.getNumberOfPages();
      for (let i=1;i<=totalPages;i++) {
        pdf.setPage(i); pdf.setDrawColor(...colors.lightGray); pdf.setLineWidth(0.3); pdf.line(margin,pageHeight-12,pageWidth-margin,pageHeight-12);
        pdf.setFontSize(7); pdf.setTextColor(...colors.gray); pdf.text("SoWash - Maintenance Services",margin,pageHeight-8);
        pdf.text(`Page ${i} of ${totalPages}`,pageWidth/2,pageHeight-8,{align:"center"}); pdf.text(format(new Date(),"MMM dd, yyyy"),pageWidth-margin,pageHeight-8,{align:"right"});
      }
      pdf.save(`Full_Report_${selDateStr}.pdf`);
      alert(`✅ Downloaded! included ${mergedJobs.length} extra reports.`);
    } catch (error) { console.error("PDF Error:", error); alert("Error: " + error.message); }
  };

  // ── Loading screen ────────────────────────────────────────────
  if (loading && jobs.length === 0)
    return (
      <div style={{ background: T.bg, fontFamily: T.font, display: "flex", alignItems: "center", justifyContent: "center", minHeight: "60vh" }}>
        <style>{STYLES}</style>
        <div style={{ textAlign: "center" }}>
          <div style={{ width: 44, height: 44, border: `3px solid ${T.border}`, borderTop: `3px solid ${T.blue}`, borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 14px" }} />
          <div style={{ color: T.muted, fontSize: 13 }}>Loading jobs…</div>
        </div>
      </div>
    );

  // ────────────────────────────────────────────────────────────────
  return (
    <div style={{ background: T.bg, fontFamily: T.font, color: T.text, padding: "14px 20px" }}>
      <style>{STYLES}</style>

      {/* ── PAGE HEADER ── */}
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 16, marginBottom: 18, overflow: "hidden" }}>
        <div style={{ padding: "18px 24px" }}>
          <div style={{ fontSize: 9, color: T.muted, letterSpacing: "0.18em", marginBottom: 5 }}>NOVAA · CUSTOMER PORTAL</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: "#fff", letterSpacing: "-0.03em", lineHeight: 1 }}>Job History</div>
          <div style={{ fontSize: 12, color: T.muted, marginTop: 5 }}>View your historical cleaning jobs and maintenance reports</div>
        </div>
        <div style={{ height: 2, background: "linear-gradient(90deg,#3B82F6,#22D3A5,transparent)" }} />
      </div>

      {/* ── STAT CARDS ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 14, marginBottom: 18 }}>
        {[
          { label: "Total Jobs",  value: stats.total,      sub: "All time",  color: T.blue,   Icon: Activity       },
          { label: "Completed",   value: stats.completed,  sub: "Jobs done", color: T.green,  Icon: CheckCircle    },
          { label: "Commercial",  value: stats.commercial, sub: "Cleaning",  color: T.amber,  Icon: CleaningIcon   },
          { label: "Maintenance", value: stats.maintenance,sub: "Service",   color: T.purple, Icon: BuildIcon      },
        ].map((s, i) => (
          <div key={s.label} className="jh-stat-card" style={{
            background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14,
            padding: "18px 20px", boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
            animation: `fadeUp 0.35s ${i * 0.06}s ease both`, position: "relative", overflow: "hidden",
          }}>
            <div style={{ position: "absolute", top: 0, right: 0, width: 70, height: 70, borderRadius: "0 14px 0 70px", background: `${s.color}0C` }} />
            <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 9 }}>
              <div style={{ width: 26, height: 26, borderRadius: 7, background: `${s.color}18`, border: `1px solid ${s.color}35`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <s.Icon size={12} color={s.color} />
              </div>
              <span style={{ fontSize: 9, fontWeight: 700, color: T.muted, letterSpacing: "0.12em", textTransform: "uppercase" }}>{s.label}</span>
            </div>
            <div style={{ fontSize: 34, fontWeight: 800, color: s.color, fontFamily: T.mono, letterSpacing: "-1px", lineHeight: 1 }}>{s.value}</div>
            <div style={{ fontSize: 11, color: T.muted, marginTop: 5 }}>{s.sub}</div>
          </div>
        ))}
      </div>

      {/* ── FILTER BAR ── */}
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: "14px 18px", marginBottom: 18 }}>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 10 }}>
          <div style={{ flex: 1, minWidth: 220, position: "relative" }}>
            <SearchIcon style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", color: "rgba(255,255,255,0.22)", fontSize: 17 }} />
            <input type="text" className="jh-input" placeholder="Search by site or location…" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} style={{ paddingLeft: 34 }} />
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <button className={`jh-filter-btn ${filterType==="all"         ? "jh-active-all"   : ""}`} onClick={() => setFilterType("all")}>All Types</button>
            <button className={`jh-filter-btn ${filterType==="commercial"  ? "jh-active-comm"  : ""}`} onClick={() => setFilterType("commercial")}>
              <CleaningIcon style={{ fontSize: 13 }} /> Commercial
            </button>
            <button className={`jh-filter-btn ${filterType==="maintenance" ? "jh-active-maint" : ""}`} onClick={() => setFilterType("maintenance")}>
              <BuildIcon style={{ fontSize: 13 }} /> Maintenance
            </button>
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", borderTop: `1px solid ${T.border}`, paddingTop: 10 }}>
          <button className={`jh-filter-btn ${filterStatus==="all"        ? "jh-active-all"   : ""}`} onClick={() => setFilterStatus("all")}><AssignmentIcon style={{ fontSize: 13 }} /> All Status</button>
          <button className={`jh-filter-btn ${filterStatus==="completed" ? "jh-active-done"  : ""}`} onClick={() => setFilterStatus("completed")}><CheckCircleIcon style={{ fontSize: 13 }} /> Completed</button>
          <button className={`jh-filter-btn ${filterStatus==="started"   ? "jh-active-prog"  : ""}`} onClick={() => setFilterStatus("started")}><ScheduleIcon style={{ fontSize: 13 }} /> In Progress</button>
          <button className={`jh-filter-btn ${filterStatus==="scheduled" ? "jh-active-sched" : ""}`} onClick={() => setFilterStatus("scheduled")}><CalendarIcon style={{ fontSize: 13 }} /> Scheduled</button>
        </div>
      </div>

      {/* ── BREADCRUMB + BACK ── */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        {viewMode !== "clients" && (
          <button onClick={handleBack} style={{
            display: "flex", alignItems: "center", gap: 5, padding: "7px 14px",
            background: "rgba(255,255,255,0.04)", border: `1px solid ${T.border}`,
            borderRadius: 9, cursor: "pointer", fontSize: 12, fontWeight: 600, color: T.muted,
            transition: "all 0.15s",
          }}
            onMouseEnter={e => { e.currentTarget.style.background="rgba(255,255,255,0.08)"; e.currentTarget.style.color=T.text; }}
            onMouseLeave={e => { e.currentTarget.style.background="rgba(255,255,255,0.04)"; e.currentTarget.style.color=T.muted; }}
          >
            <ArrowBackIcon style={{ fontSize: 15 }} /> Back
          </button>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: 5, flexWrap: "wrap", fontSize: 12, fontWeight: 600 }}>
          <span style={{ color: viewMode==="clients" ? T.text : T.muted, cursor: viewMode!=="clients" ? "pointer" : "default", transition: "color 0.15s" }}
            onClick={() => { if (viewMode!=="clients") { setViewMode("clients"); setSelectedClient(null); setSelectedMonth(null); setSearchQuery(""); } }}>
            All Clients
          </span>
          {viewMode !== "clients" && (
            <>
              <ChevronRight size={12} color={T.muted} />
              <span style={{ color: viewMode==="months" ? T.text : T.muted, cursor: viewMode!=="months" ? "pointer" : "default" }}
                onClick={() => { if (viewMode!=="months") { setViewMode("months"); setSelectedMonth(null); } }}>
                {selectedClient}
              </span>
            </>
          )}
          {(viewMode==="dates"||viewMode==="jobs") && (
            <>
              <ChevronRight size={12} color={T.muted} />
              <span style={{ color: viewMode==="dates" ? T.text : T.muted, cursor: viewMode==="jobs" ? "pointer" : "default" }}
                onClick={() => { if (viewMode==="jobs") { setViewMode("dates"); setSelectedDate(null); } }}>
                {selectedMonth && format(new Date(selectedMonth+"-01"), "MMMM yyyy")}
              </span>
            </>
          )}
          {viewMode==="jobs" && (
            <>
              <ChevronRight size={12} color={T.muted} />
              <span style={{ color: T.text }}>{format(new Date(selectedDate), "MMM dd")}</span>
            </>
          )}
        </div>
      </div>

      {/* ── VIEW 1: CLIENT CARDS ── */}
      {viewMode === "clients" && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(270px,1fr))", gap: 14 }}>
          {Object.keys(clientGroups).length > 0 ? (
            Object.keys(clientGroups).map((client, i) => (
              <div key={i} className="jh-nav-card" onClick={() => handleClientClick(client)} style={{ animationDelay: `${i * 0.05}s` }}>
                <div style={{ width: 46, height: 46, borderRadius: 12, background: "rgba(59,130,246,0.10)", border: "1px solid rgba(59,130,246,0.22)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <BusinessIcon style={{ color: T.blue, fontSize: 22 }} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 3 }}>{client}</div>
                  <div style={{ fontSize: 11, color: T.muted, fontFamily: T.mono }}>{clientGroups[client].length} total jobs</div>
                </div>
                <ChevronRight size={16} color={T.muted} />
              </div>
            ))
          ) : (
            <div style={{ gridColumn: "1/-1", textAlign: "center", color: T.muted, padding: "40px 0", fontSize: 13 }}>No clients found.</div>
          )}
        </div>
      )}

      {/* ── VIEW 2: MONTH CARDS ── */}
      {viewMode === "months" && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(270px,1fr))", gap: 14 }}>
          {Object.keys(monthGroups).sort((a,b) => new Date(b)-new Date(a)).map((monthKey, i) => (
            <div key={i} className="jh-nav-card" onClick={() => handleMonthClick(monthKey)} style={{ animationDelay: `${i*0.05}s` }}>
              <div style={{ width: 46, height: 46, borderRadius: 12, background: "rgba(167,139,250,0.10)", border: "1px solid rgba(167,139,250,0.22)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <EventIcon style={{ color: T.purple, fontSize: 22 }} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 3 }}>{format(new Date(monthKey+"-01"), "MMMM yyyy")}</div>
                <div style={{ fontSize: 11, color: T.muted, fontFamily: T.mono }}>{monthGroups[monthKey].length} jobs</div>
              </div>
              <ChevronRight size={16} color={T.muted} />
            </div>
          ))}
        </div>
      )}

      {/* ── VIEW 3: DATE CARDS ── */}
      {viewMode === "dates" && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(270px,1fr))", gap: 14 }}>
          {Object.keys(dateGroups).sort((a,b) => new Date(b)-new Date(a)).map((date, i) => (
            <div key={i} className="jh-nav-card" onClick={() => handleDateClick(date)} style={{ animationDelay: `${i*0.05}s` }}>
              <div style={{ width: 46, height: 46, borderRadius: 12, background: "rgba(251,191,36,0.10)", border: "1px solid rgba(251,191,36,0.22)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <DateRangeIcon style={{ color: T.amber, fontSize: 22 }} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 3 }}>{format(new Date(date), "MMMM dd, yyyy")}</div>
                <div style={{ fontSize: 11, color: T.muted, fontFamily: T.mono }}>{dateGroups[date].length} jobs · {format(new Date(date), "EEEE")}</div>
              </div>
              <ChevronRight size={16} color={T.muted} />
            </div>
          ))}
        </div>
      )}

      {/* ── VIEW 4: JOB CARDS ── */}
      {viewMode === "jobs" && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(360px,1fr))", gap: 18 }}>
          {currentJobsList.length > 0 ? currentJobsList.map((job, idx) => {
            const sc = getStatusCfg(job.status);
            const tc = getTypeCfg(job.job_type);
            return (
              <div key={job.unique_id || job.schedule_id || idx} className="jh-job-card" style={{
                background: T.surface, border: `1px solid ${tc.border}`,
                borderRadius: 18, padding: "22px", boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
                position: "relative", overflow: "hidden", animationDelay: `${idx*0.07}s`,
              }}>
                <div style={{ position: "absolute", top: 0, right: 0, width: 90, height: 90, borderRadius: "0 18px 0 90px", background: `${tc.color}09` }} />

                <div style={{
                  position: "absolute", top: 14, right: 14, padding: "3px 9px",
                  background: tc.bg, color: tc.color, border: `1px solid ${tc.border}`,
                  borderRadius: 6, fontSize: 9, fontWeight: 700, textTransform: "uppercase",
                  letterSpacing: "0.08em", fontFamily: T.mono, display: "flex", alignItems: "center", gap: 4,
                }}>
                  {job.job_type === "maintenance" ? <BuildIcon style={{ fontSize: 11 }} /> : <CleaningIcon style={{ fontSize: 11 }} />}
                  {tc.label}
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 11, marginBottom: 16, paddingRight: 90 }}>
                  <div style={{
                    width: 46, height: 46, borderRadius: 13, background: sc.bg, border: `1.5px solid ${sc.border}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 18, fontWeight: 800, color: sc.color, flexShrink: 0,
                  }}>{job.site_name.charAt(0).toUpperCase()}</div>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: T.text, lineHeight: 1.2, marginBottom: 3 }}>{job.site_name}</div>
                    <div style={{ fontSize: 11, color: T.muted }}>{job.client_name}</div>
                  </div>
                </div>

                <div style={{ marginBottom: 14 }}>
                  <span style={{
                    display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 10px",
                    background: sc.bg, color: sc.color, border: `1px solid ${sc.border}`,
                    borderRadius: 8, fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", fontFamily: T.mono,
                  }}>
                    {job.status === "completed" ? "✓" : (["started","in_progress","before_photos","after_photos"].includes(job.status) ? "⏳" : "📅")} {job.status.replace("_"," ")}
                  </span>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
                  {job.address && (
                    <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                      <div style={{ width: 28, height: 28, borderRadius: 7, background: "rgba(59,130,246,0.10)", border: "1px solid rgba(59,130,246,0.18)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <LocationIcon style={{ color: T.blue, fontSize: 14 }} />
                      </div>
                      <div><div className="jh-label" style={{ marginBottom: 1 }}>Location</div><div style={{ fontSize: 12, color: T.text, fontWeight: 600 }}>{job.address}</div></div>
                    </div>
                  )}
                  <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                    <div style={{ width: 28, height: 28, borderRadius: 7, background: "rgba(251,191,36,0.10)", border: "1px solid rgba(251,191,36,0.18)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <CalendarIcon style={{ color: T.amber, fontSize: 14 }} />
                    </div>
                    <div><div className="jh-label" style={{ marginBottom: 1 }}>Scheduled</div><div style={{ fontSize: 12, color: T.text, fontWeight: 600 }}>{format(new Date(job.scheduled_date), "MMM dd, yyyy")}</div></div>
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 9, marginBottom: 12, paddingTop: 14, borderTop: `1px solid ${T.border}` }}>
                  <div style={{ background: T.surf2, border: `1px solid ${T.border}`, borderRadius: 9, padding: "10px 12px", textAlign: "center" }}>
                    <div className="jh-label" style={{ marginBottom: 3 }}>{job.job_type === "maintenance" ? "Task" : "System"}</div>
                    <div style={{ fontSize: 14, fontWeight: 800, color: T.text }}>
                      {job.job_type === "maintenance" ? (job.task_name || "Maint.") : `${job.system_size} kW`}
                    </div>
                  </div>
                  <div style={{ background: T.surf2, border: `1px solid ${T.border}`, borderRadius: 9, padding: "10px 12px", textAlign: "center" }}>
                    <div className="jh-label" style={{ marginBottom: 3 }}>Service</div>
                    <div style={{ fontSize: 14, fontWeight: 800, color: T.text, fontFamily: T.mono }}>#{job.service_number}</div>
                  </div>
                </div>

                <div style={{ marginBottom: 14 }}><JobEfficiencyTimer job={job} /></div>

                <button onClick={() => handleViewDetails(job)} style={{
                  width: "100%", padding: "11px", border: "none", borderRadius: 11, cursor: "pointer",
                  fontFamily: T.font, fontSize: 13, fontWeight: 700, color: "#fff",
                  background: job.job_type === "maintenance"
                    ? "linear-gradient(135deg,#A78BFA,#7C3AED)"
                    : "linear-gradient(135deg,#3B82F6,#22D3A5)",
                  boxShadow: job.job_type === "maintenance"
                    ? "0 4px 14px rgba(167,139,250,0.3)"
                    : "0 4px 14px rgba(59,130,246,0.3)",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
                  transition: "opacity 0.15s",
                }}
                  onMouseEnter={e => e.currentTarget.style.opacity="0.88"}
                  onMouseLeave={e => e.currentTarget.style.opacity="1"}
                >
                  <ViewIcon style={{ fontSize: 17 }} /> View Details
                </button>
              </div>
            );
          }) : (
            <div style={{ gridColumn: "1/-1", textAlign: "center", padding: "40px 0", color: T.muted, fontSize: 13 }}>
              No jobs found for this date.
            </div>
          )}
        </div>
      )}


      {/* ══════════════════════════════════════════
          JOB DETAILS MODAL (READ ONLY)
      ══════════════════════════════════════════ */}
      {detailsOpen && (
        <Overlay onClick={() => setDetailsOpen(false)}>
          <div onClick={e => e.stopPropagation()} style={{
            background: T.surface, border: `1px solid ${T.bord2}`, borderRadius: 20,
            maxWidth: 720, width: "100%", maxHeight: "92vh", overflow: "hidden",
            boxShadow: "0 28px 80px rgba(0,0,0,0.70)", animation: "modalIn 0.2s ease both",
            display: "flex", flexDirection: "column",
          }}>

            {/* Modal header */}
            <div style={{ padding: "18px 22px", borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "flex-start", background: "rgba(255,255,255,0.02)", flexShrink: 0 }}>
              <div style={{ flex: 1, marginRight: 12 }}>
                {selectedJob && (
                  <>
                    <div style={{ fontSize: 9, color: T.muted, letterSpacing: "0.18em", marginBottom: 4 }}>
                      {selectedJob.job_type === "maintenance" ? "MAINTENANCE SERVICE" : "COMMERCIAL CLEANING"}
                    </div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: "#fff", letterSpacing: "-0.02em", marginBottom: 6 }}>{selectedJob.site_name}</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      {(() => { const sc = getStatusCfg(selectedJob.status); return (
                        <span style={{ padding: "3px 9px", background: sc.bg, color: sc.color, border: `1px solid ${sc.border}`, borderRadius: 6, fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", fontFamily: T.mono }}>
                          {selectedJob.status.replace("_"," ")}
                        </span>
                      ); })()}
                    </div>
                  </>
                )}
              </div>
              <CloseBtn onClick={() => setDetailsOpen(false)} />
            </div>
            <div style={{ height: 2, background: selectedJob?.job_type === "maintenance" ? "linear-gradient(90deg,#A78BFA,#7C3AED,transparent)" : "linear-gradient(90deg,#3B82F6,#22D3A5,transparent)", flexShrink: 0 }} />

            {/* Modal body */}
            <div style={{ flex: 1, overflowY: "auto", padding: "20px 22px" }}>
              {detailsLoading ? (
                <div style={{ textAlign: "center", padding: "60px 20px" }}>
                  <div style={{ width: 40, height: 40, border: `3px solid ${T.border}`, borderTop: `3px solid ${T.blue}`, borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 14px" }} />
                  <div style={{ color: T.muted, fontSize: 13 }}>Loading details…</div>
                </div>
              ) : selectedJob && (
                <>
                  {/* SITE INFORMATION */}
                  <div style={{ background: T.surf2, border: `1px solid ${T.border}`, borderRadius: 14, padding: "16px 18px", marginBottom: 16 }}>
                    <SecHead icon={LocationIcon} label="Site Information" color={T.blue} />
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                      <FieldCell label="Client" value={selectedJob.client_name} />
                      <FieldCell label="Job Type" value={selectedJob.job_type==="maintenance" ? "Maintenance Service" : "Commercial Cleaning"} />
                      {selectedJob.system_size > 0 && <FieldCell label="System Size" value={`${selectedJob.system_size} kW`} />}
                      <FieldCell label="Service Number" value={`#${selectedJob.service_number}`} />
                      {selectedJob.contact_person && <FieldCell label="Contact Person" value={selectedJob.contact_person} />}
                      {selectedJob.contact_number && <FieldCell label="Contact Number" value={selectedJob.contact_number} />}
                      {selectedJob.team_lead_name  && <FieldCell label="Team Lead"      value={selectedJob.team_lead_name} />}
                      {selectedJob.address && (
                        <FieldCell label="Address" value={`${selectedJob.address}, ${selectedJob.city}${selectedJob.state ? ", "+selectedJob.state : ""}`} span />
                      )}
                    </div>
                  </div>

                  {/* JOB TIMELINE */}
                  <div style={{ background: T.surf2, border: `1px solid ${T.border}`, borderRadius: 14, padding: "16px 18px", marginBottom: 16 }}>
                    <SecHead icon={TimeIcon} label="Job Timeline" color={T.blue} />
                    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                        <div className="jh-timeline-dot" style={{ background: "rgba(59,130,246,0.12)", border: "1.5px solid rgba(59,130,246,0.30)" }}>
                          <CalendarIcon style={{ color: T.blue, fontSize: 15 }} />
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: T.blue, marginBottom: 2 }}>Scheduled</div>
                          <div style={{ fontSize: 11, color: T.muted, fontFamily: T.mono }}>{format(new Date(selectedJob.scheduled_date), "MMM dd, yyyy")}</div>
                        </div>
                      </div>
                      {[
                        { label: "Site Arrival",       key: "reached_location_at",             color: T.amber,  IconC: LocationIcon  },
                        { label: "Work Area Reached",  key: "started_at",                      color: T.green,  IconC: ScheduleIcon  },
                        { label: "Work Started",       key: "before_photos_at", fallbackKey: "before_photo_at", color: T.purple, IconC: PhotoIcon },
                        { label: "Work Completed",     key: "after_photos_at",  fallbackKey: "after_photo_at",  color: T.pink,   IconC: PhotoIcon },
                        { label: "Site Exit",          key: "completed_at",                    color: T.green,  IconC: CheckCircleIcon },
                      ].map(item => {
                        const timestamp = selectedJob[item.key] || selectedJob[item.fallbackKey];
                        if (!timestamp) return null;
                        return (
                          <div key={item.key} style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                            <div className="jh-timeline-dot" style={{ background: `${item.color}12`, border: `1.5px solid ${item.color}30` }}>
                              <item.IconC style={{ color: item.color, fontSize: 15 }} />
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 11, fontWeight: 700, color: item.color, marginBottom: 2 }}>{item.label}</div>
                              <div style={{ fontSize: 11, color: T.muted, fontFamily: T.mono }}>
                                {format(new Date(timestamp), "MMM dd, yyyy • hh:mm a")}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* PHOTOS */}
                  {(selectedJob.before_photos || selectedJob.after_photos || selectedJob.before_photo_url || selectedJob.after_photo_url) && (
                    <div style={{ background: T.surf2, border: `1px solid ${T.border}`, borderRadius: 14, padding: "16px 18px", marginBottom: 16 }}>
                      <SecHead icon={PhotoIcon} label="Photo Gallery" color={T.purple} />
                      
                      {/* Before Photos */}
                      {(() => {
                        const rawB = selectedJob.before_photos || selectedJob.before_photo_url;
                        let bPhotos = [];
                        try { bPhotos = typeof rawB === 'string' && rawB.includes('[') ? JSON.parse(rawB) : (rawB ? [rawB] : []); } catch(e) { bPhotos = []; }
                        if (bPhotos.length === 0) return null;
                        return (
                          <div style={{ marginBottom: 16 }}>
                            <div style={{ fontSize: 10, fontWeight: 700, color: T.muted, letterSpacing: "0.10em", textTransform: "uppercase", marginBottom: 10 }}>BEFORE PHOTOS</div>
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(130px,1fr))", gap: 10 }}>
                              {bPhotos.map((photo, idx) => (
                                <img key={idx} src={cleanPhotoUrl(photo)} alt={`Before ${idx+1}`}
                                  style={{ width: "100%", height: 120, objectFit: "cover", borderRadius: 10, cursor: "pointer", border: `1px solid ${T.border}` }}
                                  onClick={() => window.open(cleanPhotoUrl(photo), "_blank")}
                                />
                              ))}
                            </div>
                          </div>
                        );
                      })()}

                      {/* After Photos */}
                      {(() => {
                        const rawA = selectedJob.after_photos || selectedJob.after_photo_url;
                        let aPhotos = [];
                        try { aPhotos = typeof rawA === 'string' && rawA.includes('[') ? JSON.parse(rawA) : (rawA ? [rawA] : []); } catch(e) { aPhotos = []; }
                        if (aPhotos.length === 0) return null;
                        return (
                          <div>
                            <div style={{ fontSize: 10, fontWeight: 700, color: T.muted, letterSpacing: "0.10em", textTransform: "uppercase", marginBottom: 10 }}>AFTER PHOTOS</div>
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(130px,1fr))", gap: 10 }}>
                              {aPhotos.map((photo, idx) => (
                                <img key={idx} src={cleanPhotoUrl(photo)} alt={`After ${idx+1}`}
                                  style={{ width: "100%", height: 120, objectFit: "cover", borderRadius: 10, cursor: "pointer", border: `1px solid ${T.border}` }}
                                  onClick={() => window.open(cleanPhotoUrl(photo), "_blank")}
                                />
                              ))}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {/* CONDITIONAL REPORT SECTION */}
                  {selectedJob.status === "completed" && (
                    selectedJob.job_type === "maintenance" ? (
                      /* MAINTENANCE REPORT */
                      <div style={{ background: "rgba(167,139,250,0.06)", border: "1px solid rgba(167,139,250,0.22)", borderRadius: 14, padding: "16px 18px", marginBottom: 16 }}>
                        <SecHead icon={EngineeringIcon} label="Maintenance Report" color={T.purple} />
                        <div style={{ marginBottom: 14 }}>
                          <div className="jh-label">Task Name</div>
                          <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{selectedJob.task_name || "General Maintenance"}</div>
                        </div>
                        {(selectedJob.task_description || selectedJob.maintenance_notes) && (
                          <div style={{ marginBottom: 14 }}>
                            <div className="jh-label">Description &amp; Notes</div>
                            <div style={{ fontSize: 13, color: T.muted, lineHeight: 1.6 }}>{selectedJob.task_description || selectedJob.maintenance_notes}</div>
                          </div>
                        )}
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 9 }}>
                          {[["Safety Check","PASSED"],["System Status","VERIFIED"]].map(([lbl,val]) => (
                            <div key={lbl} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "rgba(52,211,153,0.06)", border: "1px solid rgba(52,211,153,0.20)", borderRadius: 9, padding: "9px 12px" }}>
                              <span style={{ fontSize: 12, fontWeight: 600, color: T.muted }}>{lbl}</span>
                              <span style={{ fontSize: 10, fontWeight: 800, color: T.green, fontFamily: T.mono, background: "rgba(52,211,153,0.12)", padding: "2px 7px", borderRadius: 5 }}>{val}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      /* COMMERCIAL FSR */
                      <div style={{ background: T.surf2, border: `1px solid ${T.border}`, borderRadius: 14, padding: "16px 18px", marginBottom: 16 }}>
                        <SecHead icon={DescriptionIcon} label="Field Service Report" color={T.blue} />
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
                          <div className="jh-field-cell"><div className="jh-label">Cable Condition</div><div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{selectedJob.cable_condition||"N/A"}</div></div>
                          <div className="jh-field-cell"><div className="jh-label">Cable Quantity</div><div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{selectedJob.cable_quantity||"N/A"}</div></div>
                          <div className="jh-field-cell"><div className="jh-label">Panel Damage</div><div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{selectedJob.panel_damage||"N/A"}</div></div>
                          <div className="jh-field-cell"><div className="jh-label">Panel Brand</div><div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{selectedJob.panel_brand||"N/A"}</div></div>
                          <div className="jh-field-cell"><div className="jh-label">Inverter Alarm</div><div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{selectedJob.inverter_alarm||"N/A"}</div></div>
                          <div className="jh-field-cell"><div className="jh-label">Alarm Code</div><div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{selectedJob.alarm_code||"N/A"}</div></div>
                          <div className="jh-field-cell"><div className="jh-label">Potential Shading</div><div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{selectedJob.potential_shading||"N/A"}</div></div>
                          <div className="jh-field-cell"><div className="jh-label">Total Panels Cleaned</div><div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{selectedJob.total_panels_cleaned||"N/A"}</div></div>
                        </div>

                        {selectedJob.potential_shading==="Yes" && (
                          <div className="jh-field-cell" style={{ marginBottom: 12 }}>
                            <div className="jh-label">Shading Details</div>
                            <div style={{ fontSize: 13, color: T.muted, lineHeight: 1.6 }}>{selectedJob.shading_details||"No details provided"}</div>
                          </div>
                        )}

                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 9, marginTop: 4 }}>
                          {[
                            { label: "Rusting",       key: "rusting",        icon: "🔧" },
                            { label: "Bird Dropping", key: "bird_dropping",  icon: "🦅" },
                            { label: "Moss & Debris", key: "mos_and_debris", icon: "🍃" },
                            { label: "Earthing",      key: "earthing",       icon: "⚡" },
                          ].map(field => {
                            const active = selectedJob[field.key] || false;
                            return (
                              <div key={field.key} className={`jh-check-cell ${active ? "active-check" : ""}`}>
                                <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                                  <span style={{ fontSize: 16 }}>{field.icon}</span>
                                  <span style={{ fontSize: 12, fontWeight: 600, color: T.muted }}>{field.label}</span>
                                </div>
                                <span style={{ fontSize: 14, fontWeight: 700, color: active ? T.green : "rgba(255,255,255,0.20)" }}>{active ? "✓" : "✗"}</span>
                              </div>
                            );
                          })}
                        </div>

                        <div className="jh-field-cell" style={{ marginTop: 12 }}>
                          <div className="jh-label">Additional Notes</div>
                          <div style={{ fontSize: 13, color: T.muted, lineHeight: 1.6 }}>{selectedJob.additional_notes||"No additional notes"}</div>
                        </div>
                      </div>
                    )
                  )}

                  {/* CUSTOMER NOTES */}
                  {selectedJob.notes && (
                    <div style={{ background: "rgba(251,191,36,0.06)", border: "1px solid rgba(251,191,36,0.22)", borderRadius: 14, padding: "16px 18px", marginBottom: 16 }}>
                      <SecHead icon={NotesIcon} label="Notes" color={T.amber} />
                      <div style={{ fontSize: 13, color: T.muted, lineHeight: 1.6 }}>{selectedJob.notes}</div>
                    </div>
                  )}

                  {/* CUSTOMER SIGNATURE */}
                  {selectedJob.customer_signature && (
                    <div className="jh-field-cell" style={{ marginBottom: 16 }}>
                      <div className="jh-label" style={{ marginBottom: 8 }}>Customer Signature</div>
                      <img
                        src={selectedJob.customer_signature.startsWith("http") ? selectedJob.customer_signature : `https://app.sowashusa.com/uploads/signatures/${selectedJob.customer_signature}`}
                        alt="Customer Signature"
                        style={{ width: "100%", maxWidth: 280, height: 90, objectFit: "contain", border: `1px solid ${T.border}`, borderRadius: 9, background: "rgba(255,255,255,0.03)" }}
                      />
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Modal footer */}
            <div style={{ padding: "13px 22px", borderTop: `1px solid ${T.border}`, display: "flex", gap: 10, background: "rgba(255,255,255,0.01)", flexShrink: 0 }}>
              <button className="jh-btn-ghost" style={{ flex: 1 }} onClick={() => setDetailsOpen(false)}>Close</button>
              <button className="jh-btn-primary" style={{ flex: 1 }} onClick={handleDownloadReport}>
                <DownloadIcon style={{ fontSize: 16 }} /> Download Report
              </button>
            </div>
          </div>
        </Overlay>
      )}
    </div>
  );
};

export default CustomerJobHistory;